import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Alert,
  Snackbar,
  List,
  ListItem,
  Divider,
  Chip,
  Card,
  CardContent,
  Grid,
  InputAdornment,
  Tooltip,
  Badge
} from '@mui/material';
import {
  Search,
  QuestionAnswer,
  Reply,
  Delete,
  Close,
  Send,
  Refresh,
  Person,
  Store
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { green, blue, grey, red } from '@mui/material/colors';
import pb from './pocketbase';

// Styled components
const QuestionCard = styled(Card)(({ theme, answered }) => ({
  marginBottom: theme.spacing(2),
  borderLeft: `4px solid ${answered ? green[500] : blue[500]}`,
  transition: 'transform 0.2s',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4]
  }
}));

const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    right: -3,
    top: 13,
    padding: '0 4px',
  },
}));

const QnaManagement = () => {
  // Current date/time and user from props
  const currentDateTime = "2025-04-03 07:36:28";
  const currentUser = "MdAbuNafisNiloy";

  // State management
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [answerDialogOpen, setAnswerDialogOpen] = useState(false);
  const [answer, setAnswer] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [filter, setFilter] = useState('all'); // 'all', 'answered', 'unanswered'

  // Fetch questions from PocketBase
  const fetchQuestions = async () => {
    try {
      setLoading(true);
      
      // Build filter
      let filterString = '';
      if (searchQuery) {
        filterString = `question~"${searchQuery}" || product~"${searchQuery}" || userName~"${searchQuery}" || shopName~"${searchQuery}"`;
      }
      
      if (filter === 'answered') {
        filterString = filterString ? `${filterString} && answer != ""` : `answer != ""`;
      } else if (filter === 'unanswered') {
        filterString = filterString ? `${filterString} && answer = ""` : `answer = ""`;
      }
      
      // Fetch data
      const result = await pb.collection('qna').getList(1, 100, {
        filter: filterString,
        sort: '-created',
      });
      
      setQuestions(result.items);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching questions:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load questions',
        severity: 'error'
      });
      setLoading(false);
    }
  };

  // Load data when component mounts or dependencies change
  useEffect(() => {
    fetchQuestions();
  }, [searchQuery, filter]);

  // Open answer dialog
  const handleAnswerClick = (question) => {
    setSelectedQuestion(question);
    setAnswer(question.answer || '');
    setAnswerDialogOpen(true);
  };

  // Submit answer
  const handleSubmitAnswer = async () => {
    try {
      setIsSubmitting(true);
      
      await pb.collection('qna').update(selectedQuestion.id, {
        answer: answer.trim()
      });
      
      setSnackbar({
        open: true,
        message: 'Answer submitted successfully',
        severity: 'success'
      });
      
      fetchQuestions();
      setAnswerDialogOpen(false);
      setIsSubmitting(false);
    } catch (error) {
      console.error('Error submitting answer:', error);
      setSnackbar({
        open: true,
        message: 'Failed to submit answer',
        severity: 'error'
      });
      setIsSubmitting(false);
    }
  };

  // Open delete confirmation dialog
  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setDeleteDialogOpen(true);
  };

  // Delete question
  const handleDeleteConfirm = async () => {
    try {
      setIsSubmitting(true);
      
      await pb.collection('qna').delete(deleteId);
      
      setSnackbar({
        open: true,
        message: 'Question deleted successfully',
        severity: 'success'
      });
      
      fetchQuestions();
      setDeleteDialogOpen(false);
      setIsSubmitting(false);
    } catch (error) {
      console.error('Error deleting question:', error);
      setSnackbar({
        open: true,
        message: 'Failed to delete question',
        severity: 'error'
      });
      setIsSubmitting(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get unanswered count
  const getUnansweredCount = () => {
    return questions.filter(q => !q.answer).length;
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Product Q&A
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage customer questions and answers about products
          </Typography>
        </Box>
      </Box>

      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search questions, products, or users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
              <Button
                variant={filter === 'all' ? 'contained' : 'outlined'}
                onClick={() => setFilter('all')}
                size="small"
              >
                All
              </Button>
              <Button
                variant={filter === 'unanswered' ? 'contained' : 'outlined'}
                onClick={() => setFilter('unanswered')}
                size="small"
                startIcon={
                  <StyledBadge badgeContent={getUnansweredCount()} color="error">
                    <QuestionAnswer />
                  </StyledBadge>
                }
              >
                Unanswered
              </Button>
              <Button
                variant={filter === 'answered' ? 'contained' : 'outlined'}
                onClick={() => setFilter('answered')}
                size="small"
              >
                Answered
              </Button>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={() => {
                  setSearchQuery('');
                  setFilter('all');
                  fetchQuestions();
                }}
                size="small"
              >
                Refresh
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Questions List */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
          <CircularProgress />
        </Box>
      ) : questions.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <QuestionAnswer sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No Questions Found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {searchQuery || filter !== 'all' ? 
              'Try adjusting your search or filters' : 
              'There are no product questions to manage yet'}
          </Typography>
        </Paper>
      ) : (
        <Box>
          {questions.map((question) => (
            <QuestionCard 
              key={question.id} 
              variant="outlined"
              answered={!!question.answer}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Typography variant="h6" component="div">
                    {question.product}
                  </Typography>
                  <Box>
                    <Chip 
                      label={question.answer ? 'Answered' : 'Unanswered'} 
                      color={question.answer ? 'success' : 'primary'}
                      size="small"
                    />
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Person fontSize="small" sx={{ color: 'text.secondary', mr: 0.5 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
                    {question.userName}
                  </Typography>
                  <Store fontSize="small" sx={{ color: 'text.secondary', mr: 0.5 }} />
                  <Typography variant="body2" color="text.secondary">
                    {question.shopName}
                  </Typography>
                </Box>
                
                <Box sx={{ 
                  p: 2, 
                  bgcolor: 'background.default', 
                  borderRadius: 1,
                  mb: 2
                }}>
                  <Typography variant="body1" gutterBottom>
                    <strong>Q:</strong> {question.question}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Asked on {formatDate(question.created)}
                  </Typography>
                </Box>
                
                {question.answer && (
                  <Box sx={{ 
                    p: 2, 
                    bgcolor: green[50], 
                    borderRadius: 1,
                    mb: 2
                  }}>
                    <Typography variant="body1" gutterBottom>
                      <strong>A:</strong> {question.answer}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Answered on {formatDate(question.updated)}
                    </Typography>
                  </Box>
                )}
                
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                  <Button
                    variant={question.answer ? 'outlined' : 'contained'}
                    size="small"
                    startIcon={<Reply />}
                    onClick={() => handleAnswerClick(question)}
                  >
                    {question.answer ? 'Edit Answer' : 'Answer'}
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={<Delete />}
                    onClick={() => handleDeleteClick(question.id)}
                  >
                    Delete
                  </Button>
                </Box>
              </CardContent>
            </QuestionCard>
          ))}
        </Box>
      )}

      {/* Answer Dialog */}
      <Dialog 
        open={answerDialogOpen} 
        onClose={() => !isSubmitting && setAnswerDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        {selectedQuestion && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">
                  Answer Question
                </Typography>
                <IconButton onClick={() => setAnswerDialogOpen(false)} disabled={isSubmitting}>
                  <Close />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  <strong>Product:</strong> {selectedQuestion.product}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Customer:</strong> {selectedQuestion.userName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Shop:</strong> {selectedQuestion.shopName}
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ 
                p: 2, 
                bgcolor: 'background.default', 
                borderRadius: 1,
                mb: 3
              }}>
                <Typography variant="body1">
                  <strong>Question:</strong> {selectedQuestion.question}
                </Typography>
              </Box>
              
              <TextField
                fullWidth
                label="Your Answer"
                multiline
                rows={5}
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                placeholder="Type your answer here..."
                variant="outlined"
                disabled={isSubmitting}
              />
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button
                onClick={() => setAnswerDialogOpen(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmitAnswer}
                disabled={isSubmitting || !answer.trim()}
                startIcon={isSubmitting ? <CircularProgress size={20} /> : <Send />}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Answer'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !isSubmitting && setDeleteDialogOpen(false)}
      >
        <DialogTitle>
          Delete Question
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Are you sure you want to delete this question and answer? This action cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : <Delete />}
          >
            {isSubmitting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default QnaManagement;